/**
 * Domain Layer - TimeRange ValueObject
 * 診療予約時間帯の値オブジェクト定義
 */

/**
 * 時間範囲を表す値オブジェクト
 * 日付形式: YYYY-MM-DD
 * 時刻形式: HH:mm
 */
export interface TimeRange {
  /** 開始日付（YYYY-MM-DD形式） */
  readonly startDate: string;
  /** 開始時刻（HH:mm形式） */
  readonly startTime: string;
  /** 終了日付（YYYY-MM-DD形式） */
  readonly endDate: string;
  /** 終了時刻（HH:mm形式） */
  readonly endTime: string;
}

/**
 * 時間帯パターンを表す型
 * 実行時刻に基づいて適切な診療時間帯を決定するために使用
 */
export interface TimeRangePattern {
  /** パターンID */
  readonly id: number;
  /** 実行時刻（HH:mm形式）- この時刻以降に実行される処理に適用 */
  readonly executionTime: string;
  /** 診療開始時刻（HH:mm形式） */
  readonly startTime: string;
  /** 診療終了時刻（HH:mm形式） */
  readonly endTime: string;
}

/**
 * 診療時間帯パターン定数（8パターン）
 * 実行時刻に基づいて適切な診療時間帯を自動選択するために使用
 */
export const TIME_RANGE_PATTERNS: readonly TimeRangePattern[] = [
  { id: 1, executionTime: '06:16', startTime: '07:00', endTime: '08:15' },
  { id: 2, executionTime: '07:16', startTime: '08:15', endTime: '10:15' },
  { id: 3, executionTime: '09:46', startTime: '10:30', endTime: '12:45' },
  { id: 4, executionTime: '12:16', startTime: '13:00', endTime: '15:15' },
  { id: 5, executionTime: '14:46', startTime: '15:30', endTime: '17:45' },
  { id: 6, executionTime: '16:46', startTime: '18:00', endTime: '19:45' },
  { id: 7, executionTime: '18:46', startTime: '20:00', endTime: '21:45' },
  { id: 8, executionTime: '19:46', startTime: '22:00', endTime: '22:45' },
] as const;

/**
 * TimeRange値オブジェクトを生成する
 * @param startDate 開始日付（YYYY-MM-DD形式）
 * @param startTime 開始時刻（HH:mm形式）
 * @param endDate 終了日付（YYYY-MM-DD形式）
 * @param endTime 終了時刻（HH:mm形式）
 * @returns TimeRange値オブジェクト
 */
export const createTimeRange = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string
): TimeRange => ({
  startDate,
  startTime,
  endDate,
  endTime,
});

/**
 * 空のTimeRangeを生成する
 * @returns 空のTimeRange値オブジェクト
 */
export const createEmptyTimeRange = (): TimeRange => ({
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
});

/**
 * TimeRangeをDateオブジェクトに変換する（開始日時）
 * @param timeRange TimeRange値オブジェクト
 * @returns 開始日時のDateオブジェクト（無効な場合はnull）
 */
export const toStartDate = (timeRange: TimeRange): Date | null => {
  if (!timeRange.startDate || !timeRange.startTime) {
    return null;
  }

  const dateTimeString = `${timeRange.startDate}T${timeRange.startTime}:00`;
  const date = new Date(dateTimeString);

  return isNaN(date.getTime()) ? null : date;
};

/**
 * TimeRangeをDateオブジェクトに変換する（終了日時）
 * @param timeRange TimeRange値オブジェクト
 * @returns 終了日時のDateオブジェクト（無効な場合はnull）
 */
export const toEndDate = (timeRange: TimeRange): Date | null => {
  if (!timeRange.endDate || !timeRange.endTime) {
    return null;
  }

  const dateTimeString = `${timeRange.endDate}T${timeRange.endTime}:00`;
  const date = new Date(dateTimeString);

  return isNaN(date.getTime()) ? null : date;
};

/**
 * TimeRangeを表示用文字列に変換する
 * @param timeRange TimeRange値オブジェクト
 * @returns 表示用文字列（例: "2024-01-01 09:00 〜 2024-01-01 12:00"）
 */
export const formatTimeRange = (timeRange: TimeRange): string => {
  if (!timeRange.startDate || !timeRange.startTime || !timeRange.endDate || !timeRange.endTime) {
    return '未設定';
  }

  return `${timeRange.startDate} ${timeRange.startTime} 〜 ${timeRange.endDate} ${timeRange.endTime}`;
};

/**
 * 時刻文字列を分単位の数値に変換する
 * @param time 時刻文字列（HH:mm形式）
 * @returns 分単位の数値
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * 分単位の数値を時刻文字列に変換する
 * @param minutes 分単位の数値
 * @returns 時刻文字列（HH:mm形式）
 */
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};
